import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
// @ts-ignore
import { getHeroForCustomer } from "../../services/heroService";
import { getContactForCustomer } from "../../services/settingService";
// @ts-ignore
import { useAutoSEO } from '../../hooks/useSEOFromRoute';

interface HeroSection {
  id: string;
  page: string;
  badge?: {
    text: string;
    icon: string;
  };
  heading: string;
  subheading?: string;
  description?: string;
  backgroundImage?: string;
  buttons: Array<{
    text: string;
    url: string;
    actionType: 'modal' | 'url' | 'scroll';
    style: 'primary' | 'secondary' | 'outline';
    order: number;
    isVisible: boolean;
  }>;
  isActive: boolean;
}

export default function PrivacyPolicyPage() {
  // Auto fetch SEO from database based on route
  useAutoSEO();

  const [activeSection, setActiveSection] = useState("introduction");
  const [isVisible, setIsVisible] = useState(false);
  const [heroSection, setHeroSection] = useState<HeroSection | null>(null);
  const [contactData, setContactData] = useState<any>(null);

  const sectionRefs = {
    introduction: useRef<HTMLElement>(null),
    collection: useRef<HTMLElement>(null),
    usage: useRef<HTMLElement>(null),
    sharing: useRef<HTMLElement>(null),
    security: useRef<HTMLElement>(null),
    cookies: useRef<HTMLElement>(null),
    rights: useRef<HTMLElement>(null),
    children: useRef<HTMLElement>(null),
    changes: useRef<HTMLElement>(null),
    contact: useRef<HTMLElement>(null),
  };

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  // Fetch hero and contact data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [heroResponse, contactResponse] = await Promise.all([
          getHeroForCustomer('privacy-policy'),
          getContactForCustomer()
        ]);
        
        if (heroResponse.success && heroResponse.data) {
          const heroData = {
            ...heroResponse.data,
            isActive: heroResponse.data.isActive !== undefined ? heroResponse.data.isActive : true,
            buttons: heroResponse.data.buttons || []
          };
          setHeroSection(heroData);
        }

        if (contactResponse.success && contactResponse.data) {
          setContactData(contactResponse.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      Object.entries(sectionRefs).forEach(([key, ref]) => {
        if (ref.current) {
          const { offsetTop, offsetHeight } = ref.current;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(key);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const ref = sectionRefs[sectionId as keyof typeof sectionRefs];
    if (ref.current) {
      const offsetTop = ref.current.offsetTop - 100;
      window.scrollTo({ top: offsetTop, behavior: "smooth" });
    }
  };

  const tableOfContents = [
    { id: "introduction", title: "Giới thiệu", icon: "ri-information-line" },
    {
      id: "collection",
      title: "Thu thập thông tin",
      icon: "ri-file-list-line",
    },
    { id: "usage", title: "Sử dụng thông tin", icon: "ri-settings-3-line" },
    { id: "sharing", title: "Chia sẻ thông tin", icon: "ri-share-line" },
    { id: "security", title: "Bảo mật dữ liệu", icon: "ri-shield-check-line" },
    { id: "cookies", title: "Cookies", icon: "ri-file-code-line" },
    { id: "rights", title: "Quyền của bạn", icon: "ri-user-settings-line" },
    { id: "children", title: "Quyền riêng tư trẻ em", icon: "ri-parent-line" },
    { id: "changes", title: "Thay đổi chính sách", icon: "ri-refresh-line" },
    { id: "contact", title: "Liên hệ", icon: "ri-mail-line" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#00c897] rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute bottom-20 right-10 w-96 h-96 bg-[#00c897] rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* Breadcrumb */}
          <nav
            className={`flex items-center gap-2 text-sm text-gray-300 mb-8 transition-all duration-700 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
          >
            <Link to="/" className="hover:text-[#00c897] transition-colors">
              Trang chủ
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Chính sách bảo mật</span>
          </nav>

          <div
            className={`text-center transition-all duration-700 delay-100 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
          >
            {/* Badge */}
            {heroSection?.badge?.text ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00c897]/20 border border-[#00c897]/30 rounded-full mb-6 backdrop-blur-sm">
                {heroSection.badge.icon && <i className={`${heroSection.badge.icon} text-[#00c897]`}></i>}
                <span className="text-[#00c897] text-sm font-semibold">
                  {heroSection.badge.text}
                </span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00c897]/20 border border-[#00c897]/30 rounded-full mb-6 backdrop-blur-sm">
                <i className="ri-shield-check-line text-[#00c897]"></i>
                <span className="text-[#00c897] text-sm font-semibold">
                  Privacy Policy
                </span>
              </div>
            )}
            
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6">
              {heroSection?.heading || "Chính Sách Bảo Mật"}
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {heroSection?.description || heroSection?.subheading || "Chúng tôi cam kết bảo vệ quyền riêng tư và dữ liệu cá nhân của bạn"}
            </p>
          </div>
        </div>

        {/* Wave Decoration */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
          <svg
            className="w-full h-16"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0 C150,100 350,0 600,50 C850,100 1050,0 1200,50 L1200,120 L0,120 Z"
              fill="#F9FAFB"
            ></path>
          </svg>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24 bg-white rounded-xl shadow-lg p-6 border-2 border-gray-100">
                <h3 className="text-lg font-bold text-[#2a4b7c] mb-4 flex items-center gap-2">
                  <i className="ri-list-check text-xl"></i>
                  Mục lục
                </h3>
                <nav className="space-y-2">
                  {tableOfContents.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2 cursor-pointer ${
                        activeSection === item.id
                          ? "bg-[#00c897] text-white shadow-md"
                          : "text-[#4b5563] hover:bg-gray-100"
                      }`}
                    >
                      <i className={`${item.icon} text-lg`}></i>
                      <span className="text-sm font-medium">{item.title}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 border-2 border-gray-100">
                {/* Introduction */}
                <section
                  ref={sectionRefs.introduction}
                  className="mb-12 scroll-mt-24"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2a4b7c] rounded-lg">
                      <i className="ri-information-line text-2xl text-white"></i>
                    </div>
                    <h2 className="text-3xl font-bold text-[#2a4b7c]">
                      Giới thiệu
                    </h2>
                  </div>
                  <p className="text-[#4b5563] leading-relaxed mb-4">
                    Chào mừng bạn đến với StackWay Solution. Chúng tôi tôn trọng
                    quyền riêng tư của bạn và cam kết bảo vệ thông tin cá nhân
                    mà bạn chia sẻ với chúng tôi.
                  </p>
                  <p className="text-[#4b5563] leading-relaxed">
                    Chính sách bảo mật này giải thích cách chúng tôi thu thập,
                    sử dụng, chia sẻ và bảo vệ thông tin cá nhân của bạn khi bạn
                    sử dụng dịch vụ của chúng tôi.
                  </p>
                </section>

                {/* Collection */}
                <section
                  ref={sectionRefs.collection}
                  className="mb-12 scroll-mt-24"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2a4b7c] rounded-lg">
                      <i className="ri-file-list-line text-2xl text-white"></i>
                    </div>
                    <h2 className="text-3xl font-bold text-[#2a4b7c]">
                      Thu thập thông tin
                    </h2>
                  </div>
                  <p className="text-[#4b5563] leading-relaxed mb-6">
                    Chúng tôi thu thập các loại thông tin sau:
                  </p>
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-[#00c897]">
                      <h3 className="font-bold text-[#2a4b7c] mb-2 flex items-center gap-2">
                        <i className="ri-user-line text-[#00c897]"></i>
                        Thông tin bạn cung cấp
                      </h3>
                      <ul className="list-disc list-inside text-[#4b5563] space-y-2 ml-6">
                        <li>Họ tên, email, số điện thoại</li>
                        <li>Thông tin công ty và vị trí công việc</li>
                        <li>Nội dung liên hệ và yêu cầu dịch vụ</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-[#00c897]">
                      <h3 className="font-bold text-[#2a4b7c] mb-2 flex items-center gap-2">
                        <i className="ri-computer-line text-[#00c897]"></i>
                        Thông tin tự động thu thập
                      </h3>
                      <ul className="list-disc list-inside text-[#4b5563] space-y-2 ml-6">
                        <li>Địa chỉ IP và thông tin thiết bị</li>
                        <li>Trình duyệt và hệ điều hành</li>
                        <li>Cookies và dữ liệu sử dụng website</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Usage */}
                <section ref={sectionRefs.usage} className="mb-12 scroll-mt-24">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2a4b7c] rounded-lg">
                      <i className="ri-settings-3-line text-2xl text-white"></i>
                    </div>
                    <h2 className="text-3xl font-bold text-[#2a4b7c]">
                      Sử dụng thông tin
                    </h2>
                  </div>
                  <p className="text-[#4b5563] leading-relaxed mb-6">
                    Chúng tôi sử dụng thông tin của bạn cho các mục đích sau:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      {
                        icon: "ri-customer-service-line",
                        text: "Cung cấp và cải thiện dịch vụ",
                      },
                      {
                        icon: "ri-mail-send-line",
                        text: "Gửi thông tin và cập nhật",
                      },
                      {
                        icon: "ri-shield-check-line",
                        text: "Bảo mật và phòng chống gian lận",
                      },
                      {
                        icon: "ri-bar-chart-line",
                        text: "Phân tích và nghiên cứu thị trường",
                      },
                      {
                        icon: "ri-file-text-line",
                        text: "Tuân thủ nghĩa vụ pháp lý",
                      },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-[#00c897] transition-colors"
                      >
                        <div className="w-10 h-10 flex items-center justify-center bg-[#00c897] rounded-lg flex-shrink-0">
                          <i className={`${item.icon} text-xl text-white`}></i>
                        </div>
                        <span className="text-[#4b5563] font-medium">
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Sharing */}
                <section
                  ref={sectionRefs.sharing}
                  className="mb-12 scroll-mt-24"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2a4b7c] rounded-lg">
                      <i className="ri-share-line text-2xl text-white"></i>
                    </div>
                    <h2 className="text-3xl font-bold text-[#2a4b7c]">
                      Chia sẻ thông tin
                    </h2>
                  </div>
                  <p className="text-[#4b5563] leading-relaxed mb-4">
                    Chúng tôi không bán thông tin cá nhân của bạn. Chúng tôi chỉ
                    chia sẻ thông tin trong các trường hợp sau:
                  </p>
                  <ul className="list-disc list-inside text-[#4b5563] space-y-2 ml-6">
                    <li>Với sự đồng ý của bạn</li>
                    <li>Với các đối tác dịch vụ đáng tin cậy</li>
                    <li>Khi pháp luật yêu cầu</li>
                    <li>Để bảo vệ quyền lợi hợp pháp của chúng tôi</li>
                  </ul>
                </section>

                {/* Security */}
                <section
                  ref={sectionRefs.security}
                  className="mb-12 scroll-mt-24"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2a4b7c] rounded-lg">
                      <i className="ri-shield-check-line text-2xl text-white"></i>
                    </div>
                    <h2 className="text-3xl font-bold text-[#2a4b7c]">
                      Bảo mật dữ liệu
                    </h2>
                  </div>
                  <p className="text-[#4b5563] leading-relaxed mb-6">
                    Chúng tôi áp dụng các biện pháp bảo mật kỹ thuật và tổ chức
                    phù hợp để bảo vệ thông tin của bạn:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      {
                        icon: "ri-lock-line",
                        title: "Mã hóa SSL/TLS",
                        desc: "Bảo vệ dữ liệu truyền tải",
                      },
                      {
                        icon: "ri-server-line",
                        title: "Server bảo mật",
                        desc: "Hạ tầng được giám sát 24/7",
                      },
                      {
                        icon: "ri-shield-user-line",
                        title: "Kiểm soát truy cập",
                        desc: "Chỉ nhân viên được ủy quyền",
                      },
                      {
                        icon: "ri-file-shield-line",
                        title: "Sao lưu định kỳ",
                        desc: "Đảm bảo an toàn dữ liệu",
                      },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="bg-gradient-to-br from-[#2a4b7c]/5 to-[#00c897]/5 rounded-lg p-6 border border-gray-200 hover:border-[#00c897] transition-all hover:shadow-lg"
                      >
                        <div className="w-12 h-12 flex items-center justify-center bg-[#00c897] rounded-lg mb-4">
                          <i className={`${item.icon} text-2xl text-white`}></i>
                        </div>
                        <h3 className="font-bold text-[#2a4b7c] mb-2">
                          {item.title}
                        </h3>
                        <p className="text-sm text-[#4b5563]">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Cookies */}
                <section
                  ref={sectionRefs.cookies}
                  className="mb-12 scroll-mt-24"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2a4b7c] rounded-lg">
                      <i className="ri-file-code-line text-2xl text-white"></i>
                    </div>
                    <h2 className="text-3xl font-bold text-[#2a4b7c]">
                      Cookies
                    </h2>
                  </div>
                  <p className="text-[#4b5563] leading-relaxed mb-4">
                    Chúng tôi sử dụng cookies và công nghệ tương tự để cải thiện
                    trải nghiệm của bạn. Cookies giúp chúng tôi:
                  </p>
                  <ul className="list-disc list-inside text-[#4b5563] space-y-2 ml-6 mb-4">
                    <li>Ghi nhớ tùy chọn và cài đặt của bạn</li>
                    <li>Hiểu cách bạn sử dụng website</li>
                    <li>Cải thiện hiệu suất và bảo mật</li>
                  </ul>
                  <div className="bg-[#00c897]/10 border-l-4 border-[#00c897] rounded-lg p-6">
                    <p className="text-[#4b5563]">
                      <strong className="text-[#2a4b7c]">Lưu ý:</strong> Bạn có
                      thể quản lý cookies thông qua cài đặt trình duyệt của
                      mình.
                    </p>
                  </div>
                </section>

                {/* Rights */}
                <section
                  ref={sectionRefs.rights}
                  className="mb-12 scroll-mt-24"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2a4b7c] rounded-lg">
                      <i className="ri-user-settings-line text-2xl text-white"></i>
                    </div>
                    <h2 className="text-3xl font-bold text-[#2a4b7c]">
                      Quyền của bạn
                    </h2>
                  </div>
                  <p className="text-[#4b5563] leading-relaxed mb-6">
                    Bạn có các quyền sau đối với thông tin cá nhân của mình:
                  </p>
                  <div className="space-y-3">
                    {[
                      {
                        icon: "ri-eye-line",
                        text: "Quyền truy cập và xem thông tin",
                      },
                      {
                        icon: "ri-edit-line",
                        text: "Quyền chỉnh sửa thông tin không chính xác",
                      },
                      {
                        icon: "ri-delete-bin-line",
                        text: "Quyền yêu cầu xóa thông tin",
                      },
                      {
                        icon: "ri-forbid-line",
                        text: "Quyền phản đối xử lý thông tin",
                      },
                      {
                        icon: "ri-download-line",
                        text: "Quyền yêu cầu sao chép dữ liệu",
                      },
                      {
                        icon: "ri-mail-close-line",
                        text: "Quyền từ chối nhận email marketing",
                      },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-[#00c897] transition-colors"
                      >
                        <div className="w-10 h-10 flex items-center justify-center bg-[#2a4b7c] rounded-lg flex-shrink-0">
                          <i className={`${item.icon} text-xl text-white`}></i>
                        </div>
                        <span className="text-[#4b5563] font-medium">
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Children */}
                <section
                  ref={sectionRefs.children}
                  className="mb-12 scroll-mt-24"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2a4b7c] rounded-lg">
                      <i className="ri-parent-line text-2xl text-white"></i>
                    </div>
                    <h2 className="text-3xl font-bold text-[#2a4b7c]">
                      Quyền riêng tư trẻ em
                    </h2>
                  </div>
                  <p className="text-[#4b5563] leading-relaxed">
                    Dịch vụ của chúng tôi không dành cho trẻ em dưới 16 tuổi.
                    Chúng tôi không cố ý thu thập thông tin cá nhân từ trẻ em.
                    Nếu bạn là phụ huynh và phát hiện con bạn đã cung cấp thông
                    tin cho chúng tôi, vui lòng liên hệ để chúng tôi xóa thông
                    tin đó.
                  </p>
                </section>

                {/* Changes */}
                <section
                  ref={sectionRefs.changes}
                  className="mb-12 scroll-mt-24"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2a4b7c] rounded-lg">
                      <i className="ri-refresh-line text-2xl text-white"></i>
                    </div>
                    <h2 className="text-3xl font-bold text-[#2a4b7c]">
                      Thay đổi chính sách
                    </h2>
                  </div>
                  <p className="text-[#4b5563] leading-relaxed mb-4">
                    Chúng tôi có thể cập nhật chính sách bảo mật này theo thời
                    gian. Khi có thay đổi quan trọng, chúng tôi sẽ thông báo cho
                    bạn qua:
                  </p>
                  <ul className="list-disc list-inside text-[#4b5563] space-y-2 ml-6">
                    <li>Email đến địa chỉ bạn đã đăng ký</li>
                    <li>Thông báo trên website</li>
                    <li>Cập nhật ngày "Cập nhật lần cuối" ở đầu trang</li>
                  </ul>
                </section>

                {/* Contact */}
                <section ref={sectionRefs.contact} className="scroll-mt-24">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2a4b7c] rounded-lg">
                      <i className="ri-mail-line text-2xl text-white"></i>
                    </div>
                    <h2 className="text-3xl font-bold text-[#2a4b7c]">
                      Liên hệ
                    </h2>
                  </div>
                  <p className="text-[#4b5563] leading-relaxed mb-8">
                    Nếu bạn có bất kỳ câu hỏi nào về chính sách bảo mật này hoặc
                    muốn thực hiện quyền của mình, vui lòng liên hệ với chúng
                    tôi:
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Email */}
                    <div className="group bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] rounded-xl p-6 hover:shadow-2xl transition-all duration-300 hover:scale-105 border border-[#00c897]/20 hover:border-[#00c897]/50">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 flex items-center justify-center bg-[#00c897] rounded-xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                          <i className="ri-mail-line text-2xl text-white"></i>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-400 mb-2">Email</div>
                          {contactData?.emails && contactData.emails.length > 0 ? (
                            <div className="space-y-1">
                              {contactData.emails.map((email: any, index: number) => (
                                <a
                                  key={email._id || index}
                                  href={`mailto:${email.address}`}
                                  className="block text-white font-semibold hover:text-[#00c897] transition-colors"
                                >
                                  {email.address}
                                </a>
                              ))}
                            </div>
                          ) : (
                            <a
                              href="mailto:privacy@stackway.vn"
                              className="text-white font-semibold hover:text-[#00c897] transition-colors"
                            >
                              privacy@stackway.vn
                            </a>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="group bg-gradient-to-br from-[#00c897] to-[#00b587] rounded-xl p-6 hover:shadow-2xl transition-all duration-300 hover:scale-105">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 flex items-center justify-center bg-white rounded-xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                          <i className="ri-phone-line text-2xl text-[#00c897]"></i>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm text-white/80 mb-2">Điện thoại</div>
                          <a
                            href={`tel:${contactData?.phone || '1900xxxx'}`}
                            className="text-white font-semibold text-lg hover:underline"
                          >
                            {contactData?.phone || '1900-xxxx'}
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Address */}
                    <div className="group bg-white rounded-xl p-6 hover:shadow-2xl transition-all duration-300 hover:scale-105 border-2 border-gray-200 hover:border-[#00c897]">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] rounded-xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                          <i className="ri-map-pin-line text-2xl text-white"></i>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-500 mb-2">Địa chỉ</div>
                          <p className="text-[#2a4b7c] font-semibold leading-relaxed">
                            {contactData?.address || '123 Đường ABC, Quận 1, TP.HCM'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Working Hours */}
                    <div className="group bg-white rounded-xl p-6 hover:shadow-2xl transition-all duration-300 hover:scale-105 border-2 border-gray-200 hover:border-[#00c897]">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#00c897] to-[#00b587] rounded-xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                          <i className="ri-time-line text-2xl text-white"></i>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-500 mb-2">Giờ làm việc</div>
                          {contactData?.workingHours && Array.isArray(contactData.workingHours) ? (
                            <div className="space-y-1">
                              {contactData.workingHours.map((hour: string, index: number) => (
                                <p key={index} className="text-[#2a4b7c] font-semibold">
                                  {hour}
                                </p>
                              ))}
                            </div>
                          ) : (
                            <p className="text-[#2a4b7c] font-semibold">
                              Thứ 2 - Thứ 6: 8:00 - 17:30
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-white rounded-xl shadow-lg p-10 border-2 border-gray-100">
            <div className="w-20 h-20 flex items-center justify-center bg-[#00c897] rounded-full mx-auto mb-6">
              <i className="ri-shield-check-line text-4xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-[#2a4b7c] mb-4">
              Bảo mật là ưu tiên hàng đầu
            </h2>
            <p className="text-lg text-[#4b5563] mb-8">
              Chúng tôi cam kết bảo vệ thông tin của bạn với các tiêu chuẩn bảo
              mật cao nhất
            </p>
            <Link
              to="/lien-he"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#00c897] text-white text-base font-semibold rounded-lg hover:bg-[#00b587] transition-all duration-300 whitespace-nowrap hover:scale-105 hover:shadow-xl"
            >
              <i className="ri-customer-service-line text-xl"></i>
              Liên hệ với chúng tôi
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
